import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import MiniChart from "../../Components/Charts/MiniChart";
import { Pager } from "../DataIntegration/shared";
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DatabaseIcon,
  DownloadIcon,
  EditIcon,
  FileIcon,
  HistoryIcon,
  IdCardIcon,
  LinkIcon,
  MoreIcon,
  PlayIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  ANALYTICS,
  ANALYTICS_LABELS,
  CATEGORY_BREAKDOWN,
  POPULAR_REPORTS,
  RECENT_REPORTS,
  REPORT_ACTIONS,
  REPORT_STATS,
  REPORT_TABS,
  SCHEDULED_REPORTS,
  TOTAL_BY_CATEGORY,
  TOTAL_REPORTS,
} from "./data";
import "./Reports.css";

const STAT_ICONS = {
  file: FileIcon,
  check: CheckCircleIcon,
  clock: ClockIcon,
  download: DownloadIcon,
  users: UsersIcon,
};

const REPORT_ICONS = {
  users: UsersIcon,
  idcard: IdCardIcon,
  database: DatabaseIcon,
  shield: ShieldCheckIcon,
  link: LinkIcon,
};

const ACTION_ICONS = {
  plus: PlusIcon,
  file: FileIcon,
  clock: ClockIcon,
  settings: SettingsIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/[\s/&]+/g, "-")}`;

export default function Reports() {
  const [tab, setTab] = useState("All Reports");
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return POPULAR_REPORTS.filter((report) => {
      if (tab !== "All Reports" && report.category !== tab) return false;
      if (
        term &&
        !`${report.name} ${report.description}`.toLowerCase().includes(term)
      ) {
        return false;
      }
      return true;
    });
  }, [tab, search]);

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Reports</h1>
          <p className="page__subtitle">
            Generate, view, and export insights from your tax registry data.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <HistoryIcon size={17} />
            Scheduled Reports
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            Create Report
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Report summary">
        {REPORT_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="rp-stat h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="rp-stat__meta">
                  <p className="kpi__label">{card.label}</p>
                  <p className="rp-stat__value">
                    {card.value}
                    {card.delta && (
                      <span className="kpi__delta">
                        <ArrowUpRightIcon size={12} />
                        {card.delta}
                      </span>
                    )}
                  </p>
                  <p className="rp-stat__foot">{card.foot}</p>
                </div>
              </article>
            </div>
          );
        })}
      </section>

      <div className="rp-main">
        <div className="stack">
          {/* --------------------------------------- popular reports -- */}
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Popular Reports</h2>
              <div className="filters__search rp-search">
                <input
                  type="search"
                  placeholder="Search reports..."
                  aria-label="Search reports"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                />
                <SearchIcon size={16} />
              </div>
            </div>

            <div className="tabs tabs--scroll" role="tablist">
              {REPORT_TABS.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={tab === item}
                  className={`tabs__tab${
                    tab === item ? " tabs__tab--active" : ""
                  }`}
                  onClick={() => {
                    setTab(item);
                    setPage(1);
                  }}
                >
                  {item}
                </button>
              ))}
            </div>

            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--reports">
                <thead>
                  <tr>
                    <th>Report Name</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Last Generated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((report) => {
                    const Icon = REPORT_ICONS[report.icon];
                    return (
                      <tr key={report.id}>
                        <td>
                          <span className="rp-name">
                            <span
                              className={`rp-name__icon kpi__icon--${report.tone}`}
                            >
                              <Icon size={16} />
                            </span>
                            <button type="button" className="link">
                              {report.name}
                            </button>
                          </span>
                        </td>
                        <td>
                          <span className={chipClass(report.category)}>
                            {report.category}
                          </span>
                        </td>
                        <td className="rp-desc">{report.description}</td>
                        <td>{report.generated}</td>
                        <td>
                          <span className="row-actions">
                            <button
                              type="button"
                              className="row-actions__btn"
                              aria-label={`Run ${report.name}`}
                            >
                              <PlayIcon size={15} />
                            </button>
                            <button
                              type="button"
                              className="row-actions__btn"
                              aria-label={`Download ${report.name}`}
                            >
                              <DownloadIcon size={15} />
                            </button>
                            <button
                              type="button"
                              className="dgi-card__more"
                              aria-label={`Actions for ${report.name}`}
                            >
                              <MoreIcon size={18} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {rows.length === 0 && (
              <p className="dgi-table__empty">No reports in this category.</p>
            )}

            <Pager
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={setPerPage}
              total={TOTAL_REPORTS}
              shown={rows.length}
              noun="reports"
            />
          </section>

          {/* -------------------------------------------- analytics -- */}
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Reports Analytics (This Month)</h2>
            </div>
            <div className="row g-3">
              {ANALYTICS.map((item) => (
                <div className="col-12 col-sm-6 col-xxl-3" key={item.key}>
                  <article className="analytic h-100">
                    <p className="analytic__label">{item.label}</p>
                    <p className="analytic__value">
                      {item.value}
                      {item.noDelta ? (
                        <span className="analytic__flat">{item.note}</span>
                      ) : (
                        <>
                          <span className="kpi__delta">
                            <ArrowUpRightIcon size={12} />
                            {item.delta}
                          </span>
                          <span className="analytic__note">{item.note}</span>
                        </>
                      )}
                    </p>
                    <MiniChart
                      values={item.values}
                      labels={ANALYTICS_LABELS}
                      ticks={item.ticks}
                      color={item.color}
                      type={item.type}
                    />
                  </article>
                </div>
              ))}
            </div>
          </section>

          {/* ------------------------------------ scheduled reports -- */}
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Scheduled Reports</h2>
            </div>
            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--schedules">
                <thead>
                  <tr>
                    <th>Report Name</th>
                    <th>Schedule</th>
                    <th>Delivery Method</th>
                    <th>Recipients</th>
                    <th>Last Run</th>
                    <th>Next Run</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {SCHEDULED_REPORTS.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.schedule}</td>
                      <td>{item.delivery}</td>
                      <td>{item.recipients}</td>
                      <td>
                        {item.lastRunDate}
                        <span className="dgi-table__sub">{item.lastRunTime}</span>
                      </td>
                      <td>
                        {item.nextRunDate}
                        <span className="dgi-table__sub">{item.nextRunTime}</span>
                      </td>
                      <td>
                        <span className={chipClass(item.status)}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <span className="row-actions">
                          <button
                            type="button"
                            className="row-actions__btn"
                            aria-label={`Edit ${item.name}`}
                          >
                            <EditIcon size={15} />
                          </button>
                          <button
                            type="button"
                            className="dgi-card__more"
                            aria-label={`Actions for ${item.name}`}
                          >
                            <MoreIcon size={18} />
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="dgi-card__link">
              View all scheduled reports <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>

        {/* -------------------------------------------------- sidebar -- */}
        <div className="stack">
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Reports by Category</h2>
            </div>
            <DonutChart
              segments={CATEGORY_BREAKDOWN}
              total={TOTAL_BY_CATEGORY}
              totalLabel="Total Reports"
            />
            <button type="button" className="dgi-card__link">
              View all reports <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Recent Reports</h2>
              <button
                type="button"
                className="dgi-card__link dgi-card__link--inline"
              >
                View all
              </button>
            </div>
            <ul className="recent">
              {RECENT_REPORTS.map((report) => {
                const Icon = REPORT_ICONS[report.icon];
                return (
                  <li className="recent__row" key={report.id}>
                    <span className={`rp-name__icon kpi__icon--${report.tone}`}>
                      <Icon size={16} />
                    </span>
                    <span className="recent__meta">
                      <span className="recent__name">{report.name}</span>
                      <span className="recent__when">{report.when}</span>
                    </span>
                    <span className={chipClass(report.status)}>
                      {report.status}
                    </span>
                    <button
                      type="button"
                      className="row-actions__btn"
                      aria-label={`Download ${report.name}`}
                    >
                      <DownloadIcon size={15} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Report Actions</h2>
            </div>
            <ul className="qactions">
              {REPORT_ACTIONS.map((action) => {
                const Icon = ACTION_ICONS[action.icon];
                return (
                  <li key={action.key}>
                    <button type="button" className="qaction">
                      <span className="qaction__icon">
                        <Icon size={18} />
                      </span>
                      <span>
                        <span className="qaction__title">{action.title}</span>
                        <span className="qaction__text">{action.text}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
